import type { CookieConsentOptions, PublicUser } from '../../../common/types';
import * as ActionTypes from '../../constants';

// User Action:
export type UserAction = {
  user?: PublicUser;
  cookieConsent?: CookieConsentOptions;
  value?: number;
  currentClassroom?: { id: string; name: string };
  type: any;
};

export const user = (
  state: Partial<PublicUser> & {
    authenticated: boolean;
    // True once the first onAuthStateChanged callback has fired, whatever its outcome. Lets route
    // guards tell "auth not resolved yet" (show a loader) apart from "definitely signed out"
    // (redirect to /login), instead of treating a cold load as signed-out.
    authHydrated?: boolean;
    // TODO: use state of user from server as single source of truth:
    // Currently using redux state below, but server also has similar info.
    resetPasswordInitiate?: boolean;
    resetPasswordInvalid?: boolean;
    emailVerificationInitiate?: boolean;
    emailVerificationTokenState?: 'checking' | 'verified' | 'invalid';
    // Denormalized current-classroom pointer (name copied because students can't read classrooms/*).
    currentClassroomId?: string;
    currentClassroomName?: string;
  } = {
    authenticated: false,
    authHydrated: false
  },
  action: UserAction
) => {
  switch (action.type) {
    // Auth is ready (auth.currentUser exists) before the profile finishes loading — flip the gate so
    // data fetches can start, keeping the (cached) user fields intact until AUTH_USER refreshes them.
    case ActionTypes.AUTH_READY:
      return { ...state, authHydrated: true };
    case ActionTypes.AUTH_USER:
      return {
        ...action.user,
        authenticated: true,
        authHydrated: true
      };
    case ActionTypes.UNAUTH_USER:
      return {
        authenticated: false,
        authHydrated: true
      };
    case ActionTypes.AUTH_ERROR:
      return {
        authenticated: false,
        authHydrated: true
      };
    case ActionTypes.RESET_PASSWORD_INITIATE:
      return Object.assign({}, state, { resetPasswordInitiate: true });
    case ActionTypes.RESET_PASSWORD_RESET:
      return Object.assign({}, state, { resetPasswordInitiate: false });
    case ActionTypes.INVALID_RESET_PASSWORD_TOKEN:
      return Object.assign({}, state, { resetPasswordInvalid: true });
    case ActionTypes.EMAIL_VERIFICATION_INITIATE:
      return Object.assign({}, state, { emailVerificationInitiate: true });
    case ActionTypes.EMAIL_VERIFICATION_VERIFY:
      return Object.assign({}, state, {
        emailVerificationTokenState: 'checking'
      });
    case ActionTypes.EMAIL_VERIFICATION_VERIFIED:
      return Object.assign({}, state, {
        emailVerificationTokenState: 'verified'
      });
    case ActionTypes.EMAIL_VERIFICATION_INVALID:
      return Object.assign({}, state, {
        emailVerificationTokenState: 'invalid'
      });
    case ActionTypes.SETTINGS_UPDATED:
      return { ...state, ...action.user };
    // Keep the in-session cumulative media usage current (set from Firestore only at login otherwise),
    // so the quota gate and usage bar reflect uploads/deletes/clones without a reload. Clamp at 0.
    case ActionTypes.INCREMENT_USER_TOTAL_SIZE:
      return {
        ...state,
        totalSize: Math.max(0, (state.totalSize || 0) + (action.value || 0))
      };
    // Reflect a classroom create/join immediately (the profile doc is read only at sign-in), so the
    // top-bar classroom tag updates without a reload. currentClassroom carries the new { id, name }.
    case ActionTypes.SET_CURRENT_CLASSROOM:
      return {
        ...state,
        currentClassroomId: action.currentClassroom?.id ?? '',
        currentClassroomName: action.currentClassroom?.name ?? ''
      };
    case ActionTypes.API_KEY_REMOVED:
      return { ...state, ...action.user };
    case ActionTypes.API_KEY_CREATED:
      return { ...state, ...action.user };
    case ActionTypes.SET_COOKIE_CONSENT:
      return { ...state, cookieConsent: action.cookieConsent };
    default:
      return state;
  }
};
